/**
 * Process Service - Manage process workflows via Backend API
 * Path: /api/processes/{dbName}/{appName}
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DB_NAME = 'jayram'; // Database name for backend API

/**
 * Get all processes for an app
 */
export const getProcesses = async (appName) => {
  try {
    console.log('🔍 [processService] Fetching processes for app:', appName);
    
    const response = await fetch(`${API_BASE_URL}/processes/${DB_NAME}/${appName}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch processes');
    }
    
    // Handle both direct array response and wrapped response
    const processes = Array.isArray(data) ? data : (data.data || data.processes || []);
    console.log('✅ [processService] Fetched', processes.length, 'processes');
    
    return processes;
  } catch (error) {
    console.error('❌ [processService] Error fetching processes:', error);
    throw error;
  }
};

/**
 * Get a single process by ID
 */
export const getProcess = async (appName, processId) => {
  try {
    console.log('🔍 [processService] Fetching process:', processId, 'for app:', appName);
    
    const response = await fetch(`${API_BASE_URL}/processes/${DB_NAME}/${appName}/${processId}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch process');
    }
    
    // Handle both direct object/array response and wrapped response
    const process = Array.isArray(data) ? data[0] : (data.data || data.process || data);
    console.log('✅ [processService] Process fetched:', process?.name || processId);
    
    return process;
  } catch (error) {
    console.error('❌ [processService] Error fetching process:', error);
    throw error;
  }
};

/**
 * Create a new process
 */
export const createProcess = async (appName, processData) => {
  try {
    console.log('💾 [processService] Creating process for app:', appName, processData.name);
    
    const response = await fetch(`${API_BASE_URL}/processes/${DB_NAME}/${appName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(processData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create process');
    }
    
    const process = data.data || data.process || data;
    console.log('✅ [processService] Process created with ID:', process.id);
    
    return process;
  } catch (error) {
    console.error('❌ [processService] Error creating process:', error);
    throw error;
  }
};

/**
 * Update an existing process
 */
export const updateProcess = async (appName, processId, processData) => {
  try {
    console.log('💾 [processService] Updating process:', processId, 'for app:', appName);
    
    const response = await fetch(`${API_BASE_URL}/processes/${DB_NAME}/${appName}/${processId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(processData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update process');
    }
    
    const process = data.data || data.process || data;
    console.log('✅ [processService] Process updated successfully');
    
    return process;
  } catch (error) {
    console.error('❌ [processService] Error updating process:', error);
    throw error;
  }
};

/**
 * Delete a process
 */
export const deleteProcess = async (appName, processId) => {
  try {
    console.log('🗑️ [processService] Deleting process:', processId, 'for app:', appName);
    
    const response = await fetch(`${API_BASE_URL}/processes/${DB_NAME}/${appName}/${processId}`, {
      method: 'DELETE'
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete process');
    }
    
    console.log('✅ [processService] Process deleted successfully');
  } catch (error) {
    console.error('❌ [processService] Error deleting process:', error);
    throw error;
  }
};

/**
 * Duplicate a process
 */
export const duplicateProcess = async (appName, processId) => {
  try {
    console.log('📋 [processService] Duplicating process:', processId);
    
    const original = await getProcess(appName, processId);
    if (!original) throw new Error('Process not found');
    
    const duplicated = {
      ...original,
      name: `${original.name} (Copy)`,
      id: undefined,
      _id: undefined,
      createdAt: undefined,
      updatedAt: undefined
    };
    
    const result = await createProcess(appName, duplicated);
    console.log('✅ [processService] Process duplicated successfully');
    
    return result;
  } catch (error) {
    console.error('❌ [processService] Error duplicating process:', error);
    throw error;
  }
};

/**
 * Get process execution statistics
 */
export const getProcessStats = async (appName, processId) => {
  try {
    // Placeholder for future analytics
    return {
      totalExecutions: 0,
      completedExecutions: 0,
      averageCompletionTime: 0
    };
  } catch (error) {
    console.error('Error fetching process stats:', error);
    throw error;
  }
};



